import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as log from '../util/log';
import { storableError } from '../util/errors';
import { clearCurrentUser, fetchCurrentUser } from './user.duck';
import { createUserWithIdp, encryptConfirmPassword } from '../util/api';
import { UserTypePreference } from '../util/enums';
import { updateProfile } from '../containers/ManageAccountPage/ManageAccountPage.duck';

const authenticated = authInfo => authInfo?.isAnonymous === false;
const loggedInAs = authInfo => authInfo?.isLoggedInAs === true;

export const prepareSignupParams = async (
  params,
  encryptConfirmPasswordFn = encryptConfirmPassword
) => {
  const { confirmPassword, protectedData: originalProtectedData, ...marketplaceParams } = params;
  const { confirmPassword: legacyConfirmPassword, ...protectedData } = originalProtectedData || {};
  const plaintextConfirmPassword = confirmPassword || legacyConfirmPassword;

  if (!plaintextConfirmPassword) {
    return params;
  }

  const response = await encryptConfirmPasswordFn({
    confirmPassword: plaintextConfirmPassword,
  });
  const encryptedConfirmPassword = response?.encryptedConfirmPassword;

  if (typeof encryptedConfirmPassword !== 'string' || encryptedConfirmPassword.length === 0) {
    throw new Error('Confirmation password encryption returned an invalid value.');
  }

  return {
    ...marketplaceParams,
    protectedData: {
      ...protectedData,
      confirmPassword: encryptedConfirmPassword,
    },
  };
};

// ================ Initial State ================ //

const initialState = {
  isAuthenticated: false,

  // is marketplace operator logged in as a marketplace user
  isLoggedInAs: false,

  // scopes associated with current token
  authScopes: [],

  // auth info
  authInfoLoaded: false,

  // login
  loginError: null,
  loginInProgress: false,

  // logout
  logoutError: null,
  logoutInProgress: false,

  // signup
  signupError: null,
  signupInProgress: false,
  signupAuthorListingId: null,

  // confirm (create use with idp)
  confirmError: null,
  confirmInProgress: false,
};

// ================ Async Thunks ================ //

const authInfoThunk = createAsyncThunk('auth/authInfo', (_, thunkAPI) => {
  const { extra: sdk } = thunkAPI;
  return sdk.authInfo().catch(e => {
    // Requesting auth info just reads the token from the token
    // store (i.e. cookies), and should not fail in normal
    // circumstances. If it fails, it's due to a programming
    // error. In that case we mark the operation done and dispatch
    // `null` success action that marks the user as unauthenticated.
    log.error(e, 'auth-info-failed');
    return null;
  });
});

const loginThunk = createAsyncThunk(
  'auth/login',
  ({ username, password }, thunkAPI) => {
    const { rejectWithValue, extra: sdk, dispatch } = thunkAPI;

    return sdk
      .login({ username, password })
      .then(() => {
        return dispatch(fetchCurrentUser({ afterLogin: true }));
      })
      .then(() => ({ username, password }))
      .catch(e => rejectWithValue(storableError(e)));
  },
  {
    condition: (_, { getState }) => {
      const state = getState();
      if (authenticationInProgress(state, 'loginInProgress')) {
        return false;
      }
      return true;
    },
  }
);

const logoutThunk = createAsyncThunk(
  'auth/logout',
  (_, thunkAPI) => {
    const { rejectWithValue, extra: sdk, dispatch } = thunkAPI;

    return sdk
      .logout()
      .then(() => {
        dispatch(clearCurrentUser());
        log.clearUserId();
        return true;
      })
      .catch(e => rejectWithValue(storableError(e)));
  },
  {
    condition: (_, { getState }) => {
      const state = getState();
      if (authenticationInProgress(state, 'logoutInProgress')) {
        return false;
      }
      return true;
    },
  }
);

export const signupThunk = createAsyncThunk(
  'auth/signup',
  async (params, thunkAPI) => {
    const { rejectWithValue, extra: sdk, dispatch } = thunkAPI;
    let authorListingId = null;

    try {
      const signupParams = await prepareSignupParams(params);

      // 1. Create user in Flex
      await sdk.currentUser.create(signupParams);

      // 2. Log in user
      await dispatch(loginThunk({ username: params.email, password: params.password }));

      // 3. If PRIVATE CHEF, auto-create a *draft* listing
      if (params?.publicData?.userType === UserTypePreference.PRIVATE_CHEFS) {
        const response = await sdk.ownListings.createDraft({
          title: `${params.firstName} ${params.lastName}`,
          publicData: {
            displayName: `${params.firstName} ${params.lastName}`,
            listingType: 'private-chef-booking',
            transactionProcessAlias: 'default-booking/release-1',
            unitType: 'hour',
            type: UserTypePreference.PRIVATE_CHEFS,
          },
        });

        authorListingId = response?.data?.data?.id?.uuid;
        if (authorListingId) {
          await dispatch(
            updateProfile({
              publicData: {
                authorListingId,
                userTypePreference: UserTypePreference.PRIVATE_CHEFS,
              },
            })
          );
        }
      }

      // 4. Refresh current user so UI has updated profile & authorListingId
      await dispatch(fetchCurrentUser({ updateNotifications: false }));

      // 5. Return only non-sensitive state needed by the UI.
      return { authorListingId };
    } catch (e) {
      log.error(e, 'signup-failed', {
        email: params.email,
        firstName: params.firstName,
        lastName: params.lastName,
      });
      return rejectWithValue(storableError(e));
    }
  },
  {
    condition: (_, { getState }) => {
      const state = getState();
      if (authenticationInProgress(state, 'signupInProgress')) {
        // Cancel this thunk if a signup is already in progress
        return false;
      }
      return true;
    },
  }
);

const signupWithIdpThunk = createAsyncThunk(
  'auth/signupWithIdp',
  (params, thunkAPI) => {
    const { rejectWithValue, dispatch } = thunkAPI;
    return createUserWithIdp(params)
      .then(() => dispatch(fetchCurrentUser({ afterLogin: true })))
      .then(() => params)
      .catch(e => {
        log.error(e, 'create-user-with-idp-failed', { params });
        return rejectWithValue(storableError(e));
      });
  },
  {
    condition: (_, { getState }) => {
      const state = getState();
      if (authenticationInProgress(state, 'confirmInProgress')) {
        return false;
      }
      return true;
    },
  }
);

// ================ Slice ================ //

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {},
  extraReducers: builder => {
    // Auth Info
    builder.addCase(authInfoThunk.fulfilled, (state, action) => {
      const payload = action.payload;
      state.authInfoLoaded = true;
      state.isAuthenticated = authenticated(payload);
      state.isLoggedInAs = loggedInAs(payload);
      state.authScopes = payload?.scopes || [];
    });

    // Login
    builder
      .addCase(loginThunk.pending, state => {
        state.loginInProgress = true;
        state.loginError = null;
        state.logoutError = null;
        state.signupError = null;
        state.signupAuthorListingId = null;
      })
      .addCase(loginThunk.fulfilled, state => {
        state.loginInProgress = false;
        state.isAuthenticated = true;
      })
      .addCase(loginThunk.rejected, (state, action) => {
        state.loginInProgress = false;
        state.loginError = action.payload;
      });

    // Logout
    builder
      .addCase(logoutThunk.pending, state => {
        state.logoutInProgress = true;
        state.loginError = null;
        state.logoutError = null;
      })
      .addCase(logoutThunk.fulfilled, state => {
        state.logoutInProgress = false;
        state.isAuthenticated = false;
        state.isLoggedInAs = false;
        state.authScopes = [];
        state.signupAuthorListingId = null;
      })
      .addCase(logoutThunk.rejected, (state, action) => {
        state.logoutInProgress = false;
        state.logoutError = action.payload;
      });

    // Signup
    builder
      .addCase(signupThunk.pending, state => {
        state.signupInProgress = true;
        state.loginError = null;
        state.signupError = null;
        state.signupAuthorListingId = null;
      })
      .addCase(signupThunk.fulfilled, (state, action) => {
        state.signupInProgress = false;
        state.signupAuthorListingId = action.payload?.authorListingId || null;
      })
      .addCase(signupThunk.rejected, (state, action) => {
        state.signupInProgress = false;
        state.signupError = action.payload;
        state.signupAuthorListingId = null;
      });

    // Signup with IDP (Confirm)
    builder
      .addCase(signupWithIdpThunk.pending, state => {
        state.confirmInProgress = true;
        state.loginError = null;
        state.confirmError = null;
        state.signupAuthorListingId = null;
      })
      .addCase(signupWithIdpThunk.fulfilled, state => {
        state.confirmInProgress = false;
        state.isAuthenticated = true;
      })
      .addCase(signupWithIdpThunk.rejected, (state, action) => {
        state.confirmInProgress = false;
        state.confirmError = action.payload;
      });
  },
});

export { logoutThunk };
export default authSlice.reducer;

// ================ Selectors ================ //

export const authenticationInProgress = (state, nextInProgress = 'any') => {
  const { loginInProgress, logoutInProgress, signupInProgress, confirmInProgress } = state.auth;
  const anyInProgress =
    loginInProgress || logoutInProgress || signupInProgress || confirmInProgress;
  return nextInProgress === 'loginInProgress'
    ? loginInProgress || logoutInProgress || confirmInProgress
    : anyInProgress;
};

// ================ Thunk Wrappers ================ //
// These maintain the same API as the original thunks

export const login = (username, password) => dispatch => {
  return dispatch(loginThunk({ username, password })).unwrap();
};

export const logout = () => dispatch => {
  return dispatch(logoutThunk()).unwrap();
};

export const signup = params => dispatch => {
  return dispatch(signupThunk(params));
};

export const signupWithIdp = params => dispatch => {
  return dispatch(signupWithIdpThunk(params));
};

export const authInfo = () => dispatch => {
  return dispatch(authInfoThunk());
};
